import React from "react";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/AddOutlined";
import BookmarkIcon from "@mui/icons-material/BookmarkBorderOutlined";
import { scopeReady } from "../../shared/navigationRegistry";
import { NAV_ICON_COLOR } from "./SchoolNavMenu";

// Search results for the Dashboard launchpad. Bare rows in white type over the
// brand gradient, matching the nav menu they replace while typing. Keyboard nav
// (arrows + Enter) is driven by the parent through the focusedIndex / ref /
// handler props.
export default function DashboardSearchResults({
  results,
  focusedIndex,
  setItemRef,
  onFocus,
  onKeyDown,
  onActivate,
  onAddBookmark,
  bookmarkedIds = [],
  ctx,
}) {
  if (results.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: "rgba(255, 255, 255, 0.75)" }}>
          No matches
        </Typography>
      </Box>
    );
  }

  return (
    <List
      role="listbox"
      aria-label="Search results"
      onKeyDown={onKeyDown}
      sx={{ p: 0, display: "flex", flexDirection: "column", gap: 0.5 }}
    >
      {results.map((entry, i) => {
        const { node, breadcrumb } = entry;
        const ready = scopeReady(node, ctx);
        const focused = focusedIndex === i;
        const added = bookmarkedIds.includes(node.id);
        const lastIdx = breadcrumb.length - 1;
        return (
          <Box key={node.id}>
            <ListItemButton
              ref={(el) => setItemRef(i, el)}
              role="option"
              tabIndex={focused ? 0 : -1}
              aria-selected={focused}
              aria-disabled={!ready}
              onClick={() => onActivate(node)}
              onFocus={() => onFocus(i)}
              sx={{
                borderRadius: 1,
                py: 0.75,
                px: 0.5,
                minHeight: 36,
                opacity: ready ? 1 : 0.5,
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.12)" },
                "&:focus-visible": {
                  outline: "2px solid #FFFFFF",
                  outlineOffset: "-2px",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  rowGap: 0.25,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {breadcrumb.map((seg, idx) => {
                  const isLeaf = idx === lastIdx;
                  return (
                    <React.Fragment key={idx}>
                      {idx > 0 && (
                        <ChevronRightIcon
                          sx={{
                            fontSize: 18,
                            color: NAV_ICON_COLOR,
                            mx: 0.25,
                          }}
                        />
                      )}
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 14,
                          fontWeight: isLeaf ? 600 : 500,
                          color: isLeaf
                            ? "#FFFFFF"
                            : "rgba(255, 255, 255, 0.75)",
                        }}
                      >
                        {seg}
                      </Typography>
                    </React.Fragment>
                  );
                })}
              </Box>
              <IconButton
                size="small"
                disabled={added}
                aria-label={
                  added
                    ? `${node.label} already bookmarked`
                    : `Add ${node.label} bookmark`
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onAddBookmark(node.id);
                }}
                sx={{
                  ml: 1,
                  p: 0,
                  "&.Mui-disabled": { color: NAV_ICON_COLOR },
                }}
              >
                {added ? (
                  <BookmarkIcon sx={{ fontSize: 18, color: NAV_ICON_COLOR }} />
                ) : (
                  <AddIcon sx={{ fontSize: 18, color: "#FFFFFF" }} />
                )}
              </IconButton>
            </ListItemButton>
          </Box>
        );
      })}
    </List>
  );
}
